import { 
  BrowserRouter as Router,
  Route,
  Routes,
  useSearchParams
} from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Grid, Box, Button, Typography, Card, CardContent, List, ListItem, ListItemText, Alert, TextField, Skeleton, Collapse, IconButton, Snackbar, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HotelIcon from '@mui/icons-material/Hotel';



// Form-specific translations/helpers
const formTranslations = {
  en: {
    title: 'Who will be joining us?',
    save: 'Save Response',
    success: 'Response saved successfully!',
    error: 'Something went wrong. Please try again!',
    dietaryLabel: 'Dietary Restrictions',
    make_present: 'Contribute with a gift',
    hide_present: 'Hide bank details',
    bank_title: 'Bank details',
    bank_info: 'Beneficiary: David Philip Petri & Greta Toni\nIBAN: IE30 REVO 9903 6090 8425 39\nBIC: REVOIE23',
    hotel: 'Hotel Suggestions',
  },
  de: {
    title: 'Wer wird sich uns anschließen?',
    save: 'Antwort Speichern',
    success: 'Antwort erfolgreich gespeichert!',
    error: 'Etwas ist schief gelaufen. Bitte versuche es erneut!',
    dietaryLabel: 'Diät-/Allergiehinweise',
    make_present: 'Trage bei mit einem Geschenk',
    hide_present: 'Bankdaten ausblenden',
    bank_title: 'Bankverbindung',
    bank_info: 'Begünstigter: David Philip Petri & Greta Toni\nIBAN: IE30 REVO 9903 6090 8425 39\nBIC: REVOIE23',
    hotel: 'Hotelempfehlungen',
  },
  it: {
    title: 'Chi si unirà a noi?',
    save: 'Salva Risposta',
    success: 'Risposta salvata con successo!',
    error: 'Si è verificato un errore. Per favore riprova!',
    dietaryLabel: 'Restrizioni alimentari',
    make_present: 'Contribuisci con un regalo',
    hide_present: 'Nascondi informazioni bancarie',
    bank_title: 'Coordinate bancarie',
    bank_info: 'Beneficiario: David Philip Petri & Greta Toni\nIBAN: IE30 REVO 9903 6090 8425 39\nBIC: REVOIE23',
    hotel: 'Suggerimenti Hotel',
  }
};
const formGetText = (key, lang) => {
  const code = (lang || 'en').toLowerCase();
  const dict = formTranslations[code] || formTranslations.en;
  return dict[key] || formTranslations.en[key] || key;
};

// Normalize language from google sheet expected values: 'de', 'it', 'en'
const normalizeLang = (data) => {
  const raw = (data?.lang || '').toString().trim().toLowerCase();
  if (raw === 'de') return 'de';
  if (raw === 'it') return 'it';
  if (raw === 'en' || raw === 'en') return 'en';
  return 'en';
};

// Parse bank_info into structured lines [{ label, value, full }]
const parseBankInfo = (bankInfo) => {
  if (!bankInfo) return [];
  return bankInfo.split('\n').map(line => {
    const parts = line.split(':');
    if (parts.length >= 2) {
      const label = parts[0].trim();
      const value = parts.slice(1).join(':').trim();
      return { label, value, full: line };
    }
    return { label: '', value: line.trim(), full: line };
  });
};

const UncheckedX = ({ size = 35 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="1" y="1" width="22" height="22" rx="6" stroke="#F28B82" strokeWidth="1.6" fill="transparent" />
    <path d="M7 7L17 17M17 7L7 17" stroke="#F28B82" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckedBox = ({ size = 35 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="1" y="1" width="22" height="22" rx="6" fill="#9FD9B1" />
    <path d="M7.5 12.5L10.5 15.5L16.5 9.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const QuestionMark = ({ size = 35 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="1" y="1" width="22" height="22" rx="6" stroke="#A89080" strokeWidth="1.6" fill="transparent" />
    <text x="12" y="18" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#A89080" fontFamily="Arial, sans-serif">?</text>
  </svg>
);

const baseUrl = process.env.REACT_APP_API_BASE_URL;


const Home = ({ lang, onLangChange }) => {
    // placeholder; real folder is computed from uiLang (defaults to 'en' while loading)
    let folder = '/content_en';

    // --- DataForm state and behavior (migrated into Home) ---
    const [searchParams, ] = useSearchParams();
    const hashId = searchParams.get("id");
    const [loading, setLoading] = useState(true);

    const [data, setData] = useState(null);
    const [coming, setComing] = useState([]);
    const [dietary, setDietary] = useState('');
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formLang, setFormLang] = useState('en');
    // UI language used while rendering (defaults to English while loading)
    const uiLang = loading ? 'en' : formLang;
    const [showBank, setShowBank] = useState(false);
    // snackbar/copy state
    const [copiedMsg, setCopiedMsg] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Compute folder from the current UI language (uiLang)
    const codeForFolder = (uiLang || 'en').toLowerCase();
    if (codeForFolder === 'it') folder = '/content_it';
    else if (codeForFolder === 'de') folder = '/content_de';
    else folder = '/content_en';

    // copy helper using Clipboard API with fallback
    const copyToClipboard = async (text, label) => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        setCopiedMsg(label ? `${label} copied` : 'Copied');
        setSnackbarOpen(true);
        setTimeout(() => setSnackbarOpen(false), 2000);
      } catch (err) {
        console.error('Copy failed', err);
      }
    };

    useEffect(() => {
        setLoading(true);
        fetch(`${baseUrl}/get-data?hash=${hashId}`)
            .then((res) => {
                if (!res.ok) throw new Error("Network response was not ok");
                return res.json();
            })
            .then((data) => {
                setData(data);
                const code = normalizeLang(data);
                setFormLang(code);
                if (onLangChange) onLangChange(code);
            })
            .catch((err) => {
                console.error("Failed to load product", err);
            })
            .finally(() => setLoading(false));
    }, [hashId, onLangChange]);

    const participants = (() => {
        if (!data) return [];
        return data.group.split(',').map(p => p.trim()).filter(Boolean);
    })();

    useEffect(() => {
        if (!data) {
            setComing([]);
            setDietary('');
        } else if (!data.coming) {
            setComing(new Array(participants.length).fill(-1));
            setDietary(data.dietary || '');
        } else {
            setComing(data.coming.split(',').map(c => c.trim()).filter(Boolean).map(c => c === 'yes' ? 1 : (c === 'maybe' ? -1 : 0)));
            setDietary(data.dietary || '');
        }
    }, [data, participants.length]);

    useEffect(() => {
        let successTimer;
        if (success) {
            successTimer = setTimeout(() => setSuccess(false), 5000);
        }
        return () => {
            if (successTimer) clearTimeout(successTimer);
        };
    }, [success]);

    useEffect(() => {
        let errorTimer;
        if (error) {
            errorTimer = setTimeout(() => setError(false), 5000);
        }
        return () => {
            if (errorTimer) clearTimeout(errorTimer);
        };
    }, [error]);

    const handleComingChange = (idx) => {
        setComing(prev => {
            const updated = [...prev];
            // Cycle between: -1 (maybe) -> 1 (yes) -> 0 (no) -> -1 (maybe) -> ...
            if (updated[idx] === -1) {
                updated[idx] = 1;
            } else if (updated[idx] === 1) {
                updated[idx] = 0;
            } else {
                updated[idx] = -1;
            }
            return updated;
        });
    };

    const handleSave = async () => {
        if (!data) return;
        data.coming = coming.map(c => c === 1 ? 'yes' : (c === -1 ? 'maybe' : 'no')).join(', ');
        data.dietary = dietary;
        setLoading(true);
        const response = await fetch(`${baseUrl}/update-data`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            setError(true);
        } else {
            setSuccess(true);
        }
        setLoading(false);
    };

    return (
        <Box
            sx={{
                width: '100vw',
                minHeight: '100vh',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#ffffff',
            }}
        >
            <Grid
                container
                direction="column"
                spacing={0}
                alignItems="center"
                justifyContent="top-center"
                sx={{
                    mt: 6,
                    mb: "20%",
                    overflowY: 'auto',
                    minHeight: '100vh',
                }}
            >
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/start.jpg`} alt="Start IMG" style={{ width: '100%', height: 'auto', maxWidth: 600 }} />
                    </Box>
                </Grid>
                <Grid item>
                    {loading ? (
                        <Card sx={{
                            minWidth: "70%",
                            width: "100vw",
                            maxWidth: 600,
                            borderRadius: 0,
                            boxShadow: 'none',
                        }}>
                            {<Alert severity="info" variant="filled" sx={{ visibility: 'hidden' }}>Placeholder</Alert>}
                            <CardContent>
                                <Skeleton variant="text" width="60%" height={48} sx={{ mx: 'auto' }} />
                                <Box mt={2}>
                                    <Skeleton variant="rectangular" height={200} />
                                </Box>
                                <Box mt={2}>
                                    <Skeleton variant="rectangular" height={80} />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Skeleton variant="rectangular" width={160} height={40} />
                                </Box>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card sx={{
                            minWidth: "70%",
                            width: "100vw",
                            maxWidth: 600,
                            borderRadius: 0,
                            boxShadow: 'none',
                            mt: -3,
                        }}>
                            {error && <Alert severity="error" variant="filled" sx={{ visibility: error ? 'visible' : 'hidden' }}>{formGetText('error', uiLang)}</Alert>}
                            {success && <Alert severity="success" variant="filled" sx={{ visibility: success ? 'visible' : 'hidden' }}>{formGetText('success', uiLang)}</Alert>}
                            {!error && !success && <Alert severity="info" variant="filled" sx={{ visibility: 'hidden' }}>Placeholder</Alert>}
                            <CardContent>
                                <Typography
                                    color="text.primary"
                                    variant="h4"
                                    component="div"
                                    sx={{ textAlign: 'center', fontWeight: 400, mx: 1 }}
                                >
                                        {formGetText('title', uiLang)}
                                </Typography>

                                <Box mt={2} >
                                    <List disablePadding >
                                        {participants.map((name, idx) => (
                                            <ListItem
                                                key={idx}
                                                divider
                                                onClick={() => handleComingChange(idx)}
                                                sx={{
                                                    borderRadius: 2,
                                                    mb: 1,
                                                    background: 'rgba(245,245,245,0.8)',
                                                    cursor: 'pointer',
                                                    width: '100%',
                                                }}
                                                secondaryAction={
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {coming[idx] === 1 && <CheckedBox />}
                                                        {coming[idx] === 0 && <UncheckedX />}
                                                        {coming[idx] === -1 && <QuestionMark />}
                                                    </Box>
                                                }
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography sx={{fontWeight: "600", fontSize:26, textAlign: 'center'}}>{name}</Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>

                                <Box mt={3}>
                                    <TextField
                                        label={formGetText('dietaryLabel', uiLang)}
                                        value={dietary}
                                        onChange={(e) => setDietary(e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        maxRows={4}
                                        variant="outlined"
                                        sx={{ fontSize: 24 }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        sx={{
                                            fontSize: 30,
                                            background: "#876445",
                                            color: '#fff',
                                            fontWeight: "700",
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        <Typography sx={{fontWeight: "700", fontSize:26, textAlign: 'center'}}>{formGetText('save', uiLang)}</Typography>

                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/scroll.jpg`} alt="Scroll IMG" style={{ width: '100vw', height: 'auto', maxWidth: 600 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/std.png`} alt="Save the date IMG" style={{ width: '100vw', height: 'auto', maxWidth: 600 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/timeline.png`} alt="Timeline IMG" style={{ width: '100vw', height: 'auto', maxWidth: 1000, marginTop: 30 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/disc.png`} alt="Disc IMG" style={{ width: '100vw', height: 'auto', maxWidth: 600, marginBottom: 30 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                            src={`${folder}/info2.png`}
                            alt="Info IMG"
                            onClick={() => copyToClipboard('44.457668, 10.420735', 'Coordinates')}
                            style={{ cursor: 'pointer', width: '100vw', height: 'auto', maxWidth: 600 }}
                        />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <iframe
                            title="Wedding location on Google Maps"
                            src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2749.2784641338662!2d10.420232995134661!3d44.4577472127194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2sit!4v1776888812655!5m2!1sen!2sit%22"
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            style={{ borderRadius: 8, width: '90vw', maxWidth: 600, height: 350, border: 0, marginBottom: 30 }}
                        />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/letter_1.png`} alt="Letter 1 IMG" style={{ width: '100vw', height: 'auto', maxWidth: 600, pb: 0 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: 600 }}>
                        <img src={`${folder}/letter_2.png`} alt="Letter 2 IMG" style={{ width: '100vw', height: 'auto', maxWidth: 600, pt: 0 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={() => setShowBank(prev => !prev)}
                            sx={{
                                fontSize: 30,
                                background: "#876445",
                                color: '#fff',
                                fontWeight: "700",
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            <Typography sx={{fontWeight: "700", fontSize:26, textAlign: 'center'}}>{showBank ? formGetText('hide_present', uiLang) : formGetText('make_present', uiLang)}</Typography>
                        </Button>
                    </Box>
                </Grid>
                <Grid item sx={{ mb: 8 }}>
                        <Collapse in={showBank}>
                            <Box sx={{ mt: 2, width: '100%', maxWidth: 600 }}>
                                <Card variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="h6">{formGetText('bank_title', uiLang)}</Typography>
                                    {/* Render individual bank fields with copy buttons */}
                                    {parseBankInfo(formGetText('bank_info', uiLang)).map((line, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                            <Box sx={{ flex: 1, pr: 2 }}>
                                                {line.label ? (
                                                    <Typography variant="subtitle2">{line.label}</Typography>
                                                ) : null}
                                                <Typography sx={{ fontFamily: 'monospace', fontSize: (line.label && ['iban','bic'].includes(line.label.toLowerCase())) ? '1.15rem' : '1rem' }}>{line.value}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Tooltip title="Copy">
                                                    <IconButton edge="end" aria-label={`copy-${idx}`} onClick={() => copyToClipboard(line.value || line.full, line.label)}>
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ))}
                                </Card>
                            </Box>
                        </Collapse>
                        <Snackbar
                            open={snackbarOpen}
                            message={copiedMsg}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            onClose={() => setSnackbarOpen(false)}
                            autoHideDuration={2000}
                        />
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={`${folder}/dress_code.png`} alt="Dress Code IMG" style={{ width: '100%', height: 'auto', maxWidth: 600 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 6, mb: 2 }}>
                        <HotelIcon sx={{ fontSize: 60, color: '#876445', mr: 2 }} />
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 6 }}>
                        <Button
                            variant="contained"
                            href="https://docs.google.com/spreadsheets/d/1RiisqFJZoCL-soTqIWC1h7x0qNmk0JrUAa7YV3FjJio/edit?usp=sharing"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                fontSize: 26,
                                color: "#876445",
                                border: '1px solid #876445',
                                background: '#fff',
                                fontWeight: "700",
                                textDecoration: 'none',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                }
                            }}
                            >
                            <Typography  color="#876445" variant="h4" component="div" sx={{ textAlign: 'center', maxWidth: 600, fontWeight: 600, m: 1  }}>{formGetText('hotel', uiLang)}</Typography>
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

const App = () => {
  const [lang, setLang] = useState('en');
  const handleLangChange = useCallback((newLang) => {
    setLang(newLang);
  }, []);

  return (
      <Router>
        <Routes>
          <Route path="/" element={<Home lang={lang} onLangChange={handleLangChange} />} />
        </Routes>
      </Router>
  )
}

export default App;